import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Autocomplete, Box, Button, Chip, Grid, Stack, TextField, Typography } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import GlobalWrapper from '../../../components/Wrapper/GlobalWrapper';
import GlobalTable from '../../../components/CustomTableWithPagination/GlobalTable';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import FromAndToDatePicker from '../../../components/mui/FromAndToDatePicker';
import useResponsive from '../../../Hooks/useResponsive';
import { usePersistedState } from '../../../Hooks/usePersistedState';
import { skillxAdmissionVerificationTableHead } from './utils/skillxAdmissionVerificationTableHead';
import {
  getSkillxAdmissionVerifications,
  getSkillxAttendanceBatches,
  markSkillxAdmissionVerified,
} from '../../../redux/features/skillxSlice';
import {
  SKILLX_VERIFICATION_DEFAULT_FILTERS,
  SKILLX_VERIFICATION_FILTERS,
  SKILLX_VERIFICATION_FILTERS_STORAGE_KEY,
} from './utils/skillxAdmissionVerification';
import { SX } from '../leaderboard/leaderboardData';
import StatCard from '../SkillxStatCard';

/**
 * IntervalSkillX — Admission Verification.
 *
 * After admission, every SkillX learner has to check their admission details
 * in the Student Portal (a popup they cannot skip) and either verify them or
 * report a problem to their batch CRO with a remark. This screen is the staff
 * view of that: one row per SkillX enrolment, filterable by status.
 *
 * The one action here is for "CRO Notified" rows: once the CRO has fixed what
 * the learner reported, they mark it verified on the learner's behalf. Pending
 * rows cannot be marked: they are still the learner's to check.
 *
 * Scope is decided by the server: SkillX department and Finance see every
 * enrolment, a CRO sees the batches they own.
 */
export default function SkillxAdmissionVerification() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const mdDown = useResponsive('down', 'md');

  // Status chip, batch and dates survive a refresh (per user); the grid's own
  // search and page start fresh.
  const [filters, setFilters] = usePersistedState(
    SKILLX_VERIFICATION_FILTERS_STORAGE_KEY,
    SKILLX_VERIFICATION_DEFAULT_FILTERS,
  );
  const { status, batch, fromDate, toDate } = { ...SKILLX_VERIFICATION_DEFAULT_FILTERS, ...filters };
  const setFilter = useCallback((key, value) => setFilters((prev) => ({ ...prev, [key]: value })), [setFilters]);
  const setStatus = (v) => setFilter('status', v);
  const setBatch = (v) => setFilter('batch', v);
  const setFromDate = (v) => setFilter('fromDate', v);
  const setToDate = (v) => setFilter('toDate', v);

  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    sort: { field: '', direction: '' },
    frozenColumns: [],
    hiddenColumns: [],
  });

  // The row whose "Mark verified" confirmation is open; null closes it.
  const [confirmRow, setConfirmRow] = useState(null);

  const { admissionVerification, attendance } = useSelector((state) => state.skillx);
  const { data, meta, summary, loading, marking } = admissionVerification;

  useEffect(() => {
    dispatch(getSkillxAttendanceBatches());
  }, [dispatch]);

  const dispatchparams = useMemo(
    () => ({
      paginationData: {
        pageNum: tableState.page,
        gridSize: tableState.pageSize,
        search: tableState.search,
      },
      status,
      batchId: batch?.batchId || '',
      fromDate,
      toDate,
    }),
    [tableState.page, tableState.pageSize, tableState.search, status, batch, fromDate, toDate],
  );

  useEffect(() => {
    dispatch(getSkillxAdmissionVerifications(dispatchparams));
  }, [dispatchparams, dispatch]);

  // A narrowed filter on page 6 would ask for a page that no longer exists.
  const resetToFirstPage = useCallback(() => {
    setTableState((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, []);

  const batchOptions = useMemo(
    () =>
      (attendance.batches || []).map((b) => ({
        batchId: b.course_batch_id,
        batchName: b.course_batch_name,
        courseName: b.course_name,
      })),
    [attendance.batches],
  );

  // A kept batch that is no longer in the list (closed, or no longer this
  // CRO's) would filter to nothing with no visible reason; drop it.
  useEffect(() => {
    if (batch && batchOptions.length && !batchOptions.some((o) => o.batchId === batch.batchId)) {
      setFilter('batch', null);
    }
  }, [batch, batchOptions, setFilter]);

  const handleButtonActionClick = useCallback((actionKey, row) => {
    if (actionKey === 'MARK_VERIFIED' && row) setConfirmRow(row);
  }, []);

  const handleMarkVerified = () => {
    dispatch(markSkillxAdmissionVerified({ courseEnrollmentId: confirmRow.course_enrollment_id })).then(
      (res) => {
        if (res?.payload?.status === 'success') {
          enqueueSnackbar(res.payload.message, { variant: 'success' });
          setConfirmRow(null);
          dispatch(getSkillxAdmissionVerifications(dispatchparams));
          return;
        }
        enqueueSnackbar(res?.payload?.message || 'Could not mark as verified', { variant: 'error' });
      },
    );
  };

  return (
    <GlobalWrapper title="SkillX Admission Verification">
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:users-group-rounded-bold-duotone"
            value={summary.total}
            label="SkillX enrolments"
            color={SX.blue}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:hourglass-bold-duotone"
            value={summary.pending}
            label="Waiting for the student"
            color="#C25E00"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:bell-bing-bold-duotone"
            value={summary.croNotified}
            label="Reported to CRO"
            color="#B23B3B"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:verified-check-bold-duotone"
            value={summary.verified}
            label="Verified"
            color="#2E7D62"
          />
        </Grid>
      </Grid>

      <Stack gap={2}>
        <Box sx={{ display: 'flex', flexDirection: mdDown ? 'column' : 'row', gap: 2 }}>
          <Autocomplete
            sx={{ flex: 1 }}
            options={batchOptions}
            value={batch}
            onChange={(e, v) => {
              setBatch(v);
              resetToFirstPage();
            }}
            getOptionLabel={(o) => `${o.batchName} — ${o.courseName}`}
            isOptionEqualToValue={(o, v) => o.batchId === v.batchId}
            renderOption={(props, o) => (
              <li {...props} key={o.batchId}>
                <Stack>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{o.batchName}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{o.courseName}</Typography>
                </Stack>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Batch" placeholder="All batches" />}
          />
          <Box sx={{ flex: 1 }}>
            <FromAndToDatePicker
              fullWidth
              disabled={false}
              fromLabel="Purchased From"
              toLabel="Purchased To"
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={(d) => {
                setFromDate(d);
                resetToFirstPage();
              }}
              setToDate={(d) => {
                setToDate(d);
                resetToFirstPage();
              }}
            />
          </Box>
        </Box>

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {SKILLX_VERIFICATION_FILTERS.map((f) => (
            <Chip
              key={f.key}
              label={`${f.label} (${summary[f.summaryKey] ?? 0})`}
              onClick={() => {
                setStatus(f.key);
                resetToFirstPage();
              }}
              color={status === f.key ? 'primary' : 'default'}
              variant={status === f.key ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600 }}
            />
          ))}
          {(fromDate || toDate) && (
            <Chip
              label="Clear dates"
              onClick={() => {
                setFromDate('');
                setToDate('');
                resetToFirstPage();
              }}
              variant="outlined"
              icon={<Icon icon="solar:close-circle-bold-duotone" width={16} height={16} />}
              sx={{ fontWeight: 600 }}
            />
          )}
        </Stack>

        <GlobalTable
          headers={skillxAdmissionVerificationTableHead}
          rows={data || []}
          meta={meta}
          rowUniqueId="course_enrollment_id"
          loading={loading}
          tableState={tableState}
          setTableState={setTableState}
          onButtonActionClick={handleButtonActionClick}
          handleRefresh={() => dispatch(getSkillxAdmissionVerifications(dispatchparams))}
          exportApi={getSkillxAdmissionVerifications}
          exportParams={{
            ...dispatchparams,
            paginationData: { ...dispatchparams.paginationData, pageNum: 1, gridSize: 'no' },
          }}
        />
      </Stack>

      <Typography sx={{ mt: 1, fontSize: 12.5, color: SX.textMuted }}>
        One row per SkillX enrolment. The date filter is on the purchase date; both dates have to
        be set for it to apply. Only rows reported to the CRO can be marked verified here.
      </Typography>

      <GlobalDialog
        title={`Mark as verified — ${confirmRow?.student_name || ''}`}
        open={Boolean(confirmRow)}
        setOpen={(val) => {
          if (!val) setConfirmRow(null);
        }}
        closeButton
        fullWidth
        maxWidth="sm"
        content={
          confirmRow ? (
            <Stack spacing={2}>
              <Typography sx={{ fontSize: 13.5 }}>
                {confirmRow.course_name} · Admission No {confirmRow.admn_no}
              </Typography>
              <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: '#FDE7E7' }}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>
                  What the student reported
                </Typography>
                <Typography sx={{ fontSize: 14 }}>{confirmRow.verification_remarks || '—'}</Typography>
              </Box>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                Confirm that this has been fixed. The student will not be asked to verify again.
              </Typography>
              <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                <Button onClick={() => setConfirmRow(null)} disabled={marking} sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleMarkVerified}
                  disabled={marking}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {marking ? 'Saving…' : 'Mark as verified'}
                </Button>
              </Stack>
            </Stack>
          ) : null
        }
      />
    </GlobalWrapper>
  );
}
